/**
 * @opcentral/dependr - Dependency aggregator for OpCentral apps
 * 
 * PURPOSE: Manages transitive dependencies and ensures single shared instances
 * of orchestrators across the entire application.
 * 
 * RECOMMENDED USAGE:
 *   import { ActrProvider, useActr } from '@opcentral/dependr';
 *   import { Selectr, SelectrNavigation } from '@opcentral/dependr';
 * 
 * BENEFITS:
 * - Single package versions (no conflicts)
 * - Shared singleton instances (Actr, React contexts, etc.)
 * - One dependency in package.json
 * - Tree-shaking still works via Vite path aliases
 * 
 * See USAGE.md for detailed guide and best practices.
 * 
 * NOTE: Only exports packages that are actually implemented and have proper exports.
 * This prevents Vite errors from missing packages.
 */

// Core orchestrators (VERIFIED - these exist and work)
export * from '@opcentral/actr';
export * from '@opcentral/commandr';
export * from '@opcentral/configgr';
export * from '@opcentral/backtrackr';

// UI packages (VERIFIED)
export * from '@opcentral/navigatr';
export * from '@opcentral/formr';
export * from '@opcentral/promptr';
export * from '@opcentral/skinnr';

// Specialized packages (VERIFIED)
export * from '@opcentral/draggr';
export * from '@opcentral/loggr';
export * from '@opcentral/clippr';

// Major orchestrators (CRITICAL - needed by most apps)
export * from '@opcentral/aggregatr';
export * from '@opcentral/integratr';
export * from '@opcentral/synkr';
export * from '@opcentral/pluggr';
export * from '@opcentral/profylr';

// UI/View packages (VERIFIED)
export * from '@opcentral/interfacr';
export * from '@opcentral/viewr';
export * from '@opcentral/panelr';
export * from '@opcentral/notifyr';

// Data packages (VERIFIED)
export * from '@opcentral/cachr';
export * from '@opcentral/validatr';
export * from '@opcentral/favr';
export * from '@opcentral/selectr';
export * from '@opcentral/organizr';
export * from '@opcentral/taggr';
export * from '@opcentral/translatr';
export * from '@opcentral/translatr/react'; // React UI components
export * from '@opcentral/templatr';
export * from '@opcentral/mappr';
export * from '@opcentral/outputtr';

// Document viewing (previously in interfacr)
// NOTE: Commented out due to TypeScript compilation errors in viewr dependency
// export * from '@opcentral/previewr';

// Additional packages used by apps
export * from '@opcentral/outlinr';
export * from '@opcentral/steppr';
export * from '@opcentral/searchr';
export * from '@opcentral/fylr';

// NOTE: The following are server-side only (require Node.js):
// - @opcentral/parsr (uses mailparser - Node.js only)
// Import directly in server-side code only

// NOTE: The following packages are commented out because they either:
// - Don't exist yet
// - Have incorrect package.json exports
// - Are not commonly needed
// Uncomment as packages are properly implemented

// export * from '@opcentral/inputr';
export * from '@opcentral/stylr';
// export * from '@opcentral/parsr';
// export * from '@opcentral/contextr';
// export * from '@opcentral/distillr';
// export * from '@opcentral/lockr';
// export * from '@opcentral/permittr';
// export * from '@opcentral/previewr';

// Temporary stub exports for previewr viewers (until previewr is fully integrated)
// These prevent build errors in fmwvtool-v4
export const getPDFViewer = () => {
  console.warn('[Dependr] getPDFViewer is a stub - previewr not yet integrated');
  return {
    loadDocument: async () => ({ numPages: 0 }),
    render: async () => {},
    extractText: async () => 'Previewr not yet integrated'
  };
};

export const getWordViewer = () => {
  console.warn('[Dependr] getWordViewer is a stub - previewr not yet integrated');
  return {
    loadDocument: async () => ({ pages: 0 }),
    render: async () => {},
    getHTML: async () => '<p>Previewr not yet integrated</p>'
  };
};

export const getExcelViewer = () => {
  console.warn('[Dependr] getExcelViewer is a stub - previewr not yet integrated');
  return {
    loadWorkbook: async () => ({ sheets: [] }),
    render: async () => {},
    getData: async () => []
  };
};

export const getPowerPointViewer = () => {
  console.warn('[Dependr] getPowerPointViewer is a stub - previewr not yet integrated');
  return {
    loadPresentation: async () => ({ slides: 0 }),
    render: async () => {},
    getThumbnails: async () => []
  };
};

// Simple discovery system (minimal - only verified packages)
export const find = {
  /**
   * Get a capability by name
   * @param {string} capability - What you need (e.g., 'logging', 'navigation')
   * @returns {any} The requested capability
   */
  get(capability) {
    const capabilities = {
      'actions': () => import('@opcentral/actr').then(m => m.getActr()),
      'logging': () => import('@opcentral/loggr').then(m => m.getLogger()),
      'navigation': () => import('@opcentral/navigatr').then(m => m.getNavigatr()),
      'forms': () => import('@opcentral/formr').then(m => m.getFormr()),
      'dialogs': () => import('@opcentral/promptr').then(m => m.getPromptr()),
      'theme': () => import('@opcentral/skinnr').then(m => m.getSkinnr()),
      'commands': () => import('@opcentral/commandr').then(m => m.getCommandr()),
      'config': () => import('@opcentral/configgr').then(m => m.getConfiggr()),
      'drag-drop': () => import('@opcentral/draggr').then(m => m.getDraggr()),
      'undo-redo': () => import('@opcentral/backtrackr').then(m => m.getBacktrackr()),
      'tags': () => import('@opcentral/taggr').then(m => m.getTaggr()),
    };
    
    const loader = capabilities[capability];
    if (!loader) {
      console.warn(`[Dependr] Unknown capability: ${capability}`);
      console.log('[Dependr] Available capabilities:', Object.keys(capabilities).join(', '));
      return null;
    }
    
    return loader();
  },
  
  /**
   * List all available capabilities
   * @returns {string[]} Array of capability names
   */
  list() {
    return [
      'actions', 'logging', 'navigation', 'forms', 'dialogs', 'theme',
      'commands', 'config', 'drag-drop', 'undo-redo', 'tags'
    ];
  }
};
